import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { usePatchMask } from "@/hooks/useMasks";
import { Project } from "@/types/project";
import { Mask } from "@/types/mask";
import { PcmVersion } from "@/types/pcmVersion";
import { normalizeUpdateValues, useMaskForm } from "@/hooks/useMaskForm";
import { MaskDialogContent } from "./MaskDialogContent";

type EditMaskDialogProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  pcmVersions: PcmVersion[];
  project: Project;
  mask?: Mask;
  maskId: string;
  chipPosCount: number;
};

export const EditMaskDialog = ({
  chipPosCount = 0,
  mask,
  maskId,
  onOpenChange,
  open,
  pcmVersions,
  project,
}: EditMaskDialogProps) => {
  const { mutate } = useSWRConfig();
  const { trigger: updateMask, isMutating } = usePatchMask();

  const {
    data,
    waferVisualizationData,
    selectedReferenceDie,
    setNewlyCreatedPcmVersion,
    pcmVersionsWithNewlyCreatedPcmVersion,
    handleChange,
    handleBlur,
    handleDieClick,
  } = useMaskForm({ open, mask, pcmVersions });

  const handleUpdateMask = async () => {
    try {
      const normalizedValues = normalizeUpdateValues(data);
      await updateMask(
        { ...normalizedValues, id: maskId },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully updated Mask: "${data.name}"!`);
            mutate({ url: `/projects/${project.id}/masks` });
            mutate({ url: `/masks/${mask?.id}` });
            mutate({ url: `/masks/${mask?.id}/structures` });
            onOpenChange(false);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <MaskDialogContent
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Mask"
      description="Update the mask specifications."
      submitLabel="Update Mask"
      onSubmit={handleUpdateMask}
      isMutating={isMutating}
      pcmVersionDisabled={true}
      chipPosCount={chipPosCount}
      data={data}
      waferVisualizationData={waferVisualizationData}
      selectedReferenceDie={selectedReferenceDie}
      pcmVersionsWithNewlyCreatedPcmVersion={pcmVersionsWithNewlyCreatedPcmVersion}
      setNewlyCreatedPcmVersion={setNewlyCreatedPcmVersion}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleDieClick={handleDieClick}
    />
  );
};
